// Aignoz Idea Factory — /factory (Phase 2)
// Generates 100 first-of-its-kind healthcare ideas via the 5-stage funnel.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Factory, Sparkles, Play, Loader2, CheckCircle2, XCircle, ChevronRight,
  Rocket, Download, Filter, AlertTriangle, Beaker, Cpu, Compass, ScanLine, ListChecks,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { API, formatUSD } from "@/lib";
import { formatINR } from "@/currency";

const FUNNEL_STAGES = [
  { id: "problem_discovery", label: "Problem Discovery", icon: Compass },
  { id: "bio_mapping",       label: "Bio Mapping",        icon: Beaker },
  { id: "tech_matching",     label: "Tech Matching",      icon: Cpu },
  { id: "white_space",       label: "White Space",        icon: ScanLine },
  { id: "venture_filter",    label: "Venture Filter",     icon: ListChecks },
];

const SOURCES = [
  "Invisible Biology", "Delayed Diagnosis", "Therapeutic Failure",
  "Biological Heterogeneity", "Clinical Workflow Bottlenecks", "Data Fragmentation",
  "Simulation Deficit", "Rare Disease Neglect", "Aging", "Human-Machine Interfaces",
];

const MAGENTA = "#D17EC0";

// ===========================================================================
// Hook: poll factory_runs list + currently active run details + SSE
// ===========================================================================
function useFactory(activeRunId, setActiveRunId) {
  const [runs, setRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [funnelLive, setFunnelLive] = useState({});
  const sseRef = useRef(null);

  async function refreshList() {
    try {
      const r = await fetch(`${API}/factory/runs?limit=20`);
      const j = await r.json();
      setRuns(j.runs || []);
    } catch { /* silent */ }
  }
  async function refreshActive(id) {
    if (!id) return;
    try {
      const r = await fetch(`${API}/factory/runs/${id}`);
      if (r.status === 404) { setActiveRun(null); return; }
      const j = await r.json();
      setActiveRun(j);
      setFunnelLive(j.funnel_counts || {});
    } catch { /* silent */ }
  }

  // Initial load
  useEffect(() => { refreshList(); }, []);

  // Pick latest run if none active
  useEffect(() => {
    if (!activeRunId && runs.length > 0) setActiveRunId(runs[0].run_id);
  }, [runs, activeRunId, setActiveRunId]);

  // Open SSE for active run
  useEffect(() => {
    if (!activeRunId) return;
    refreshActive(activeRunId);
    if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
    const url = `${API}/factory/runs/${activeRunId}/stream`;
    const es = new EventSource(url);
    sseRef.current = es;
    const onStage = (name) => (e) => {
      try {
        const d = JSON.parse(e.data || "{}");
        setFunnelLive((p) => ({ ...p, [d.stage]: d.count ?? p[d.stage] ?? 0, _last_event: name }));
      } catch { /* */ }
    };
    es.addEventListener("stage_started", onStage("stage_started"));
    es.addEventListener("stage_progress", onStage("stage_progress"));
    es.addEventListener("stage_complete", onStage("stage_complete"));
    es.addEventListener("run_started", () => { refreshActive(activeRunId); });
    es.addEventListener("run_complete", () => {
      refreshActive(activeRunId);
      refreshList();
      toast.success("Factory run complete", {
        description: "Top 10 ideas ready in the leaderboard.",
      });
    });
    es.addEventListener("error", () => { /* allow EventSource to reconnect */ });
    return () => { es.close(); sseRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRunId]);

  return { runs, activeRun, funnelLive, refreshList, refreshActive };
}

// ===========================================================================
// PAGE
// ===========================================================================
export default function FactoryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeRunId, setActiveRunId] = useState(null);
  const [showRunModal, setShowRunModal] = useState(false);
  const [drawerIdea, setDrawerIdea] = useState(null);
  const [cronStatus, setCronStatus] = useState(null);
  const { runs, activeRun, funnelLive, refreshList } = useFactory(activeRunId, setActiveRunId);

  useEffect(() => {
    fetch(`${API}/factory/cron-status`).then((r) => r.json()).then(setCronStatus).catch(() => {});
  }, []);

  // Phase 4 — landing page deep-links here with `?run=1` to auto-open the modal.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get("run") === "1") {
      setShowRunModal(true);
      // Strip the param so back/refresh doesn't keep reopening.
      navigate("/factory", { replace: true });
    }
  }, [location.search, navigate]);

  const isRunning = activeRun?.status === "running" || activeRun?.status === "queued";
  const totalThisRun = activeRun?.total_ideas || 0;
  const dryRunBadge = activeRun?.dry_run;

  return (
    <div className="h-full overflow-y-auto" data-testid="factory-page">
      <div className="max-w-[1280px] mx-auto px-8 py-10">
        {/* ========================  HEADER ======================== */}
        <header className="flex items-start justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.18em] font-medium"
                style={{ background: "rgba(209,126,192,0.10)", borderColor: "rgba(209,126,192,0.45)", color: "#F0B6DD" }}
              >
                <Factory className="w-3 h-3" /> Aignoz Idea Factory™
              </span>
              <span className="text-[10px] text-white/40 mono uppercase tracking-[0.16em]">Phase 2</span>
            </div>
            <h1 className="text-4xl font-semibold tracking-tight leading-tight">
              100 first-of-its-kind ideas, generated, ranked, priced.
            </h1>
            <p className="text-base text-white/55 italic mt-2 max-w-xl leading-snug">
              A systematic 5-stage funnel: problem discovery → biology → technology → white space → venture filter.
              Sellable price per idea baked in.
            </p>
          </div>

          <div className="flex flex-col items-end gap-2 shrink-0">
            <div className="flex items-center gap-2">
              {cronStatus && (
                <Badge variant="outline" className="text-[10px] mono uppercase tracking-[0.14em] text-white/55"
                  data-testid="cron-status-chip">
                  {cronStatus.enabled ? (cronStatus.mode === "scheduled" ? "Nightly 02:00 IST" : "Cron pending") : "Manual mode"}
                </Badge>
              )}
              <ExportPortfolioBtn runId={activeRunId} disabled={!activeRun} />
              <Button
                onClick={() => setShowRunModal(true)}
                disabled={isRunning}
                className="bg-teal-500 hover:bg-teal-400 text-black font-medium gap-2"
                data-testid="run-factory-btn"
              >
                <Sparkles className="w-4 h-4" /> Run Factory
              </Button>
            </div>
            <div className="text-[10px] text-white/35">All runs are LLM-budget-capped (₹2 000 / run)</div>
          </div>
        </header>

        {/* ========================  HERO STAT ======================== */}
        <HeroStat activeRun={activeRun} totalThisRun={totalThisRun} dryRun={dryRunBadge}
                  onRunClick={() => setShowRunModal(true)} hasAnyRun={runs.length > 0} />

        {/* ========================  FUNNEL VIZ ======================== */}
        {activeRun && (
          <FunnelViz funnelCounts={funnelLive} status={activeRun.status} batchSize={activeRun.batch_size} />
        )}

        {/* ========================  TOP-10 LEADERBOARD ======================== */}
        {activeRun?.status === "complete" && (
          <Leaderboard ideas={activeRun.top_10_ideas || []} onPickIdea={setDrawerIdea} />
        )}
        {activeRun && activeRun.status !== "complete" && activeRun.status !== "queued" && activeRun.status !== "running" && (
          <FailedRunBlock run={activeRun} />
        )}

        {/* ========================  RECENT RUNS ======================== */}
        {runs.length > 1 && (
          <RecentRuns runs={runs} activeRunId={activeRunId} onPick={setActiveRunId} />
        )}
      </div>

      <RunModal
        open={showRunModal}
        onClose={() => setShowRunModal(false)}
        onCreated={(id) => { setActiveRunId(id); refreshList(); }}
      />
      <IdeaDrawer idea={drawerIdea} onClose={() => setDrawerIdea(null)} />
    </div>
  );
}

// ===========================================================================
// HERO STAT
// ===========================================================================
function HeroStat({ activeRun, totalThisRun, dryRun, onRunClick, hasAnyRun }) {
  if (!hasAnyRun) {
    return (
      <div
        data-testid="factory-empty"
        className="rounded-2xl border border-white/[0.08] bg-white/[0.015] p-10 text-center mb-8"
      >
        <Factory className="w-10 h-10 mx-auto mb-3" style={{ color: MAGENTA }} />
        <div className="text-lg font-semibold">No factory runs yet</div>
        <div className="text-sm text-white/55 mt-1 max-w-md mx-auto">
          Click &ldquo;Run Factory&rdquo; to generate your first 100 ideas. Try dry-run mode first — it&apos;s free and gives a full preview.
        </div>
        <Button onClick={onRunClick} className="mt-5 bg-teal-500 hover:bg-teal-400 text-black gap-2" data-testid="empty-run-btn">
          <Sparkles className="w-4 h-4" /> Run your first factory
        </Button>
      </div>
    );
  }
  const date = activeRun?.started_at ? new Date(activeRun.started_at).toLocaleString() : "—";
  const pct = totalThisRun ? Math.min(100, Math.round((totalThisRun / Math.max(activeRun?.batch_size || 1, 1)) * 100)) : 0;
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-gradient-to-br from-white/[0.02] to-transparent p-6 mb-8 flex items-center gap-6"
      data-testid="factory-hero-stat">
      <div className="relative w-24 h-24 shrink-0">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r="42" stroke="rgba(255,255,255,0.06)" strokeWidth="6" fill="none" />
          <circle cx="50" cy="50" r="42" stroke={MAGENTA} strokeWidth="6" fill="none"
            strokeDasharray={`${(pct / 100) * 264} 264`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-lg mono font-semibold">{pct}%</div>
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1">Latest run</div>
        <div className="text-2xl font-semibold leading-tight">
          {totalThisRun > 0 ? `${totalThisRun} ideas generated` : `Generating ${activeRun?.batch_size} ideas…`}
        </div>
        <div className="flex items-center gap-3 mt-2 text-xs text-white/55">
          <span>{date}</span>
          {dryRun && <Badge variant="outline" className="text-[9px] uppercase tracking-[0.16em] border-amber-400/40 text-amber-300/85">Dry-run preview</Badge>}
          <span className={`mono uppercase tracking-[0.14em] text-[10px] ${
            activeRun?.status === "complete" ? "text-teal-300" : activeRun?.status === "failed" ? "text-rose-300" : "text-amber-300"
          }`}>
            {activeRun?.status}
          </span>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// FUNNEL VIZ
// ===========================================================================
function FunnelViz({ funnelCounts, status, batchSize }) {
  return (
    <div className="rounded-2xl border border-white/[0.08] p-6 mb-8" data-testid="factory-funnel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80">5-stage funnel</div>
          <div className="text-sm text-white/55 italic">Aignoz Idea Factory pipeline</div>
        </div>
        <div className="text-xs text-white/45 mono">target: {batchSize}</div>
      </div>
      <div className="grid grid-cols-5 gap-3">
        {FUNNEL_STAGES.map((s, i) => {
          const c = funnelCounts[s.id] || 0;
          const pct = batchSize > 0 ? Math.min(100, (c / batchSize) * 100) : 0;
          const isActive = c > 0 && c < batchSize && status === "running";
          const isDone = c >= batchSize;
          return (
            <motion.div
              key={s.id}
              data-testid={`funnel-stage-${s.id}`}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: i * 0.05 }}
              className="rounded-lg border border-white/[0.08] bg-white/[0.012] p-3"
            >
              <div className="flex items-center gap-2 mb-2">
                <s.icon className={`w-3.5 h-3.5 ${isDone ? "text-teal-300" : isActive ? "text-amber-300" : "text-white/40"}`} />
                <span className="text-[10px] uppercase tracking-[0.14em] text-white/65">{s.label}</span>
                {isActive && <Loader2 className="w-3 h-3 animate-spin text-amber-300/80 ml-auto" />}
                {isDone && <CheckCircle2 className="w-3 h-3 text-teal-300 ml-auto" />}
              </div>
              <div className="text-2xl mono font-semibold leading-none">{c}</div>
              <div className="h-1 rounded-full bg-white/[0.05] mt-2 overflow-hidden">
                <div className="h-full rounded-full transition-[width] duration-500"
                  style={{ width: `${pct}%`, background: isDone ? "rgba(94,234,212,0.7)" : "rgba(245,158,11,0.7)" }} />
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ===========================================================================
// LEADERBOARD (top 10)
// ===========================================================================
function Leaderboard({ ideas, onPickIdea }) {
  if (!ideas || ideas.length === 0) {
    return (
      <div className="rounded-2xl border border-white/[0.08] p-6 mb-8 text-sm text-white/55 italic text-center">
        Top-10 leaderboard will appear when the run completes.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-white/[0.08] p-6 mb-8" data-testid="factory-leaderboard">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80">Top 10 ranked ideas</div>
          <div className="text-sm text-white/55 italic">Sorted by Aignoz V3 Venture Filter total</div>
        </div>
        <span className="text-[10px] text-white/40 mono">click any row for full idea card</span>
      </div>
      <div className="rounded-lg border border-white/[0.06] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.14em] text-white/45 bg-white/[0.02]">
              <th className="text-left px-3 py-2 w-12">#</th>
              <th className="text-left px-3 py-2">Idea (Aignoz Formula)</th>
              <th className="text-left px-3 py-2 w-32">Source</th>
              <th className="text-right px-3 py-2 w-28">Score</th>
              <th className="text-right px-3 py-2 w-44" style={{ color: "#F0B6DD" }}>Sellable Idea Price</th>
              <th className="text-left px-3 py-2 w-40">Cluster</th>
              <th className="text-right px-3 py-2 w-32">Action</th>
            </tr>
          </thead>
          <tbody>
            {ideas.map((i) => {
              const total = i.scorecard?.venture_filter_total || 0;
              return (
                <tr key={i.idea_id}
                  data-testid={`leaderboard-row-${i.idea_id}`}
                  onClick={() => onPickIdea(i)}
                  className="border-t border-white/[0.04] hover:bg-white/[0.025] cursor-pointer">
                  <td className="px-3 py-2.5 mono text-teal-300/80">{i.rank}</td>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-white/90 line-clamp-1">{i.aignoz_formula || "(no formula)"}</div>
                    <div className="text-[11px] text-white/45 line-clamp-1">{i.problem}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-[0.12em] border-white/15 text-white/60">
                      {i.problem_source || "—"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="mono text-teal-200 font-semibold">{total}<span className="text-white/40">/100</span></div>
                    <div className="h-1 rounded-full bg-white/[0.05] mt-1 overflow-hidden w-20 ml-auto">
                      <div className="h-full rounded-full bg-gradient-to-r from-teal-400/70 to-cyan-300/70"
                        style={{ width: `${total}%` }} />
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right" data-testid={`leaderboard-price-${i.idea_id}`}>
                    <div className="mono font-semibold" style={{ color: "#F0B6DD" }}>{formatUSD(i.sellable_idea_price_usd)}</div>
                    <div className="text-[10px] mono text-white/45">
                      {i.sellable_idea_price_inr != null ? `₹${Math.round(i.sellable_idea_price_inr).toLocaleString("en-IN")}` : formatINR(i.sellable_idea_price_usd)}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-[0.12em] border-violet-400/30 text-violet-200/80">
                      {i.white_space_cluster || "—"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <PromoteButton ideaId={i.idea_id} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===========================================================================
// PROMOTE — small button per row
// ===========================================================================
function PromoteButton({ ideaId }) {
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  async function go(e) {
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await fetch(`${API}/factory/ideas/${ideaId}/promote`, { method: "POST" });
      const j = await r.json();
      if (!j.bioforge_run_id) throw new Error("no bioforge_run_id");
      toast.success("Promoted to full 12-agent venture", {
        description: "Opening workspace…", duration: 2500,
      });
      setTimeout(() => nav(`/projects/${j.bioforge_run_id}/overview`), 600);
    } catch (err) {
      toast.error("Promotion failed", { description: String(err) });
    } finally { setBusy(false); }
  }
  return (
    <Button size="sm" variant="outline" onClick={go} disabled={busy}
      data-testid={`promote-btn-${ideaId}`}
      className="h-7 px-2.5 text-[10px] uppercase tracking-[0.14em] gap-1.5">
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Rocket className="w-3 h-3" />}
      Promote
    </Button>
  );
}

// ===========================================================================
// RECENT RUNS
// ===========================================================================
function RecentRuns({ runs, activeRunId, onPick }) {
  return (
    <div className="rounded-2xl border border-white/[0.08] p-5 mb-8" data-testid="factory-recent-runs">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-3">Recent runs</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {runs.slice(0, 9).map((r) => {
          const active = r.run_id === activeRunId;
          const date = r.started_at ? new Date(r.started_at).toLocaleString() : "—";
          return (
            <button key={r.run_id} onClick={() => onPick(r.run_id)}
              data-testid={`recent-run-${r.run_id}`}
              className={`text-left rounded-lg border p-3 transition ${
                active ? "border-teal-400/40 bg-teal-400/[0.04]" : "border-white/[0.06] bg-white/[0.012] hover:border-white/15"
              }`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] mono text-white/55">{r.run_id.slice(0, 8)}…</span>
                <span className={`text-[9px] uppercase tracking-[0.14em] ${
                  r.status === "complete" ? "text-teal-300" :
                  r.status === "failed" ? "text-rose-300" :
                  r.status === "cancelled" ? "text-white/45" : "text-amber-300"
                }`}>{r.status}</span>
              </div>
              <div className="text-xs text-white/70">
                {r.total_ideas ?? 0} / {r.batch_size} ideas{r.dry_run ? " · dry" : ""}
              </div>
              <div className="text-[10px] text-white/40 mt-1">{date}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ===========================================================================
// FAILED RUN BLOCK
// ===========================================================================
function FailedRunBlock({ run }) {
  return (
    <div className="rounded-2xl border border-rose-400/30 bg-rose-500/[0.04] p-5 mb-8" data-testid="factory-failed">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-rose-300" />
        <div className="text-sm font-semibold text-rose-200">Run {run.status}</div>
      </div>
      {run.error && <div className="text-xs text-white/65 mt-2 mono">{run.error}</div>}
    </div>
  );
}

// ===========================================================================
// RUN MODAL
// ===========================================================================
function RunModal({ open, onClose, onCreated }) {
  const [batchSize, setBatchSize] = useState(100);
  const [sourceFilter, setSourceFilter] = useState("any");
  const [seed, setSeed] = useState("");
  const [dryRun, setDryRun] = useState(false);
  const [busy, setBusy] = useState(false);

  const estLLM = Math.ceil(batchSize / 10) * 5;
  const estCost = Math.round(estLLM * 15);
  const willExceed = !dryRun && estCost > 2000;

  async function submit() {
    setBusy(true);
    try {
      const body = {
        batch_size: batchSize,
        dry_run: dryRun,
        source_filter: sourceFilter === "any" ? null : sourceFilter,
        seed: seed || null,
      };
      const r = await fetch(`${API}/factory/run`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({ detail: "Unknown error" }));
        const msg = typeof j.detail === "string" ? j.detail : (j.detail?.error || "Request failed");
        toast.error("Factory run rejected", { description: msg });
        return;
      }
      const j = await r.json();
      toast.success("Factory run started", { description: `Run ${j.run_id.slice(0,8)}…` });
      onCreated(j.run_id);
      onClose();
    } catch (e) {
      toast.error("Network error", { description: String(e) });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="run-factory-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-teal-300" /> Run the Idea Factory
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 mt-2">
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <label className="text-xs uppercase tracking-[0.14em] text-white/55">Batch size</label>
              <span className="mono text-sm text-teal-200" data-testid="modal-batch-display">{batchSize}</span>
            </div>
            <Slider value={[batchSize]} min={10} max={100} step={5}
              onValueChange={(v) => setBatchSize(v[0])} data-testid="modal-batch-slider" />
            <div className="text-[10px] text-white/40 mt-1">Range 10–100 ideas (system cap)</div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-white/55 block mb-2">Source filter</label>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger data-testid="modal-source-filter">
                <SelectValue placeholder="Any" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any (sample across all 10 sources)</SelectItem>
                {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-white/55 block mb-2">Theme seed (optional)</label>
            <input type="text" value={seed} onChange={(e) => setSeed(e.target.value)}
              data-testid="modal-seed"
              placeholder="e.g. 'pediatric oncology'"
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-md px-2.5 py-1.5 text-sm" />
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <Checkbox checked={dryRun} onCheckedChange={setDryRun} data-testid="modal-dry-run" className="mt-0.5" />
            <div>
              <div className="text-sm">Dry-run (deterministic preview, no LLM cost)</div>
              <div className="text-[10px] text-white/45">Generates mock data for testing the full UI flow.</div>
            </div>
          </label>

          <div className="rounded-md border border-white/[0.08] bg-white/[0.012] p-3" data-testid="modal-cost-estimate">
            <div className="text-[10px] uppercase tracking-[0.14em] text-white/45 mb-1">Estimated cost</div>
            <div className="text-2xl mono font-semibold" style={{ color: dryRun ? "#5EEAD4" : (willExceed ? "#FB7185" : "#F0B6DD") }}>
              {dryRun ? "₹0" : `₹${estCost}`}
            </div>
            <div className="text-[10px] text-white/45 mt-1">
              ~{dryRun ? 0 : estLLM} LLM calls · cap ₹2 000
            </div>
            {willExceed && (
              <div className="text-[10px] text-rose-300 mt-1">⚠ Exceeds the ₹2 000 cost cap — request will be rejected.</div>
            )}
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || willExceed}
            className="bg-teal-500 hover:bg-teal-400 text-black gap-2"
            data-testid="modal-confirm-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            {dryRun ? "Run dry preview" : "Run factory"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// IDEA DRAWER
// ===========================================================================
function IdeaDrawer({ idea, onClose }) {
  return (
    <Sheet open={!!idea} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto" data-testid="idea-drawer">
        {idea && (
          <>
            <SheetHeader>
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="text-[10px] uppercase tracking-[0.12em]">Rank #{idea.rank}</Badge>
                <Badge variant="outline" className="text-[10px] uppercase tracking-[0.12em] border-violet-400/30 text-violet-200/80">
                  {idea.white_space_cluster}
                </Badge>
              </div>
              <SheetTitle className="text-base leading-snug">
                {idea.aignoz_formula || idea.problem}
              </SheetTitle>
            </SheetHeader>
            <div className="space-y-5 mt-5">
              <Section label="Problem">
                <p className="text-sm text-white/85">{idea.problem}</p>
                <div className="text-xs text-white/55 mt-1.5">Population: {idea.target_population || "—"}</div>
                <div className="text-xs text-white/55 mt-0.5">Source: <span className="text-white/80">{idea.problem_source}</span></div>
                <div className="text-xs text-white/55 mt-0.5 italic">{idea.severity_hint}</div>
              </Section>

              <Section label="Biology map">
                <KV k="Tissues" v={(idea.bio_map?.tissues || []).join(", ") || "—"} />
                <KV k="Pathways" v={(idea.bio_map?.pathways || []).join(", ") || "—"} />
                <KV k="Biomarkers" v={(idea.bio_map?.biomarkers || []).join(", ") || "—"} />
                <KV k="Mechanism" v={idea.bio_map?.mechanism || "—"} />
              </Section>

              <Section label="Top technologies">
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {(idea.top_techs || []).map((t) => (
                    <Badge key={t} variant="outline" className="text-[10px] border-teal-400/30 text-teal-200/85">{t}</Badge>
                  ))}
                </div>
                <KV k="Healthcare function" v={idea.healthcare_function || "—"} />
              </Section>

              <Section label="V3 Venture Filter">
                {[
                  ["disease_burden_score", "Disease Burden"],
                  ["market_size_score", "Market Size"],
                  ["scientific_feasibility_score", "Scientific Feasibility"],
                  ["patentability_score", "Patentability"],
                  ["funding_availability_score", "Funding Availability"],
                ].map(([k, label]) => {
                  const s = idea.scorecard?.[k] || {}; const v = s.score || 0;
                  return (
                    <div key={k} className="mb-2" data-testid={`drawer-bar-${k}`}>
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs text-white/80">{label}</span>
                        <span className="text-xs mono text-teal-200">{v}<span className="text-white/40">/20</span></span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden mt-0.5">
                        <div className="h-full rounded-full bg-gradient-to-r from-teal-400/70 to-cyan-300/70"
                          style={{ width: `${(v / 20) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
                <div className="flex items-baseline justify-between mt-3 pt-3 border-t border-white/[0.06]">
                  <span className="text-xs uppercase tracking-[0.14em] text-white/55">Total</span>
                  <span className="text-2xl mono font-semibold text-teal-100">
                    {idea.scorecard?.venture_filter_total ?? 0}<span className="text-sm text-white/40">/100</span>
                  </span>
                </div>
              </Section>

              <Section label="Sellable Idea Price">
                <div className="rounded-lg p-4 border"
                  style={{ background: "linear-gradient(135deg, rgba(209,126,192,0.10), rgba(94,234,212,0.04))", borderColor: "rgba(209,126,192,0.35)" }}
                  data-testid="drawer-sellable">
                  <div className="text-3xl mono font-semibold text-white">{formatUSD(idea.sellable_idea_price_usd)}</div>
                  <div className="text-base mono mt-0.5" style={{ color: "#F0B6DD" }}>
                    {idea.sellable_idea_price_inr != null ? `₹${Math.round(idea.sellable_idea_price_inr).toLocaleString("en-IN")}` : formatINR(idea.sellable_idea_price_usd)}
                  </div>
                  <div className="text-[10px] text-white/55 mt-2 leading-snug">
                    Cost saved ({formatUSD(idea.cost_saved_usd)}) ÷ Reg. team ({idea.regulatory_team_size}) = sellable price per idea
                  </div>
                </div>
              </Section>

              <div className="flex gap-2 pb-6">
                <PromoteFull idea={idea} onPromoted={onClose} />
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80 mb-2">{label}</div>
      {children}
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-white/45 w-28 shrink-0 uppercase tracking-[0.1em] text-[10px] pt-0.5">{k}</span>
      <span className="text-white/85 flex-1">{v}</span>
    </div>
  );
}

function PromoteFull({ idea, onPromoted }) {
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  if (idea.promoted_to_run_id) {
    return (
      <Button onClick={() => nav(`/projects/${idea.promoted_to_run_id}/overview`)}
        className="bg-teal-500 hover:bg-teal-400 text-black gap-2 flex-1"
        data-testid="drawer-open-promoted">
        <ChevronRight className="w-3.5 h-3.5" /> Open promoted venture
      </Button>
    );
  }
  async function go() {
    setBusy(true);
    try {
      const r = await fetch(`${API}/factory/ideas/${idea.idea_id}/promote`, { method: "POST" });
      const j = await r.json();
      if (!j.bioforge_run_id) throw new Error("no run_id");
      toast.success("Promoted — running 12-agent pipeline");
      onPromoted?.();
      setTimeout(() => nav(`/projects/${j.bioforge_run_id}/overview`), 500);
    } catch (e) {
      toast.error("Promotion failed", { description: String(e) });
    } finally { setBusy(false); }
  }
  return (
    <Button onClick={go} disabled={busy}
      className="bg-teal-500 hover:bg-teal-400 text-black gap-2 flex-1"
      data-testid="drawer-promote-btn">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
      Promote to Full Venture
    </Button>
  );
}

// ===========================================================================
// EXPORT PORTFOLIO (PDF first, falls back to JSON)
// ===========================================================================
function ExportPortfolioBtn({ runId, disabled }) {
  const [busy, setBusy] = useState(false);
  async function go() {
    if (!runId) return;
    setBusy(true);
    try {
      // Try PDF
      const r = await fetch(`${API}/factory/runs/${runId}/export.pdf`);
      if (r.ok) {
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `aignoz-portfolio-${runId.slice(0,8)}.pdf`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        return;
      }
      // 501 → fallback JSON
      const j = await fetch(`${API}/factory/runs/${runId}/ideas?limit=200`).then((x) => x.json());
      const data = JSON.stringify(j, null, 2);
      const blob = new Blob([data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `aignoz-portfolio-${runId.slice(0,8)}.json`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.info("PDF export coming soon", { description: "Downloaded as JSON for now." });
    } catch (e) {
      toast.error("Export failed", { description: String(e) });
    } finally { setBusy(false); }
  }
  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" onClick={go} disabled={disabled || busy}
            data-testid="export-portfolio-btn" className="gap-1.5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Export Portfolio
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">PDF if available; JSON otherwise.</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
